import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import { AppBar, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, CircularProgress, Chip, Typography, Box, Divider, Paper, TextField, InputAdornment } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import AIIcon from '@material-ui/icons/Memory';
import PolicyIcon from '@material-ui/icons/Security';
import VisibilityIcon from '@material-ui/icons/Visibility';
import AssessmentIcon from '@material-ui/icons/Assessment';
import HistoryIcon from '@material-ui/icons/History';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import SearchIcon from '@material-ui/icons/Search';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import IvtMetricBoxes from './IvtMetricBoxes';
import { SectionHeader } from './utils';
import LinearProgress from '@material-ui/core/LinearProgress';
import { TabPanel } from './TabPanel';
import { TIER_COLORS, TIER_LABELS, getRiskScoreColor, getIvtRateColor } from '../styles/ivt';

const MAX_DTA_PREVIEW_LENGTH = 500;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    width: `calc(100% + ${theme.spacing(4)}px)`,
    marginLeft: -theme.spacing(2),
    marginRight: -theme.spacing(2),
  },
}));

const TYPE_LABELS = {
  incoming_request: 'Incoming Requests',
  outgoing_response: 'Outgoing Responses',
  bidrequest: 'Bid Requests',
  bidresponse: 'Bid Responses',
};

const TIME_FRAME_OPTIONS = [
  { value: 1, label: 'Today (last 24h)' },
  { value: 2, label: 'Last 2 days' },
  { value: 3, label: 'Last 3 days' },
  { value: 5, label: 'Last 5 days' },
  { value: 7, label: 'Last 7 days' },
  { value: 10, label: 'Last 10 days' },
  { value: 15, label: 'Last 15 days' },
];


function formatData(data) {
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return data || '(empty)';
  }
}

function formatRowDetails (row) {
  return [
    `Timestamp:  ${row.timestamp || '\u2014'}`,
    `Publisher:  ${row.publisher_name ? `${row.publisher_name} (${row.publisher})` : row.publisher || '\u2014'}`,
    `Site:       ${row.site_name ? `${row.site_name} (${row.site})` : row.site || '\u2014'}`,
    `Domain:     ${row.tldomain || '\u2014'}`,
    `IP:         ${row.ip || '\u2014'}`,
    `Bidder:     ${row.bidder_name ? `${row.bidder_name} (${row.bidder})` : row.bidder || '\u2014'}`,
    `Endpoint:   ${row.endpoint || '\u2014'}`,
    `Method:     ${row.method || '\u2014'}`,
    `Integration: ${row.integration || '\u2014'}`,
    '',
    '--- Data ---',
    formatData(row.data),
  ].join('\n');
}

const jsonPreStyle = { backgroundColor: '#f5f5f5', color: '#333333', padding: '12px', borderRadius: '4px', overflow: 'auto', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' };

function JsonViewerDialog({ open, onClose, data, title }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(data || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title || 'Request Data'}</DialogTitle>
      <DialogContent>
        <pre style={{ ...jsonPreStyle, maxHeight: '500px' }}>
{data || '(empty)'}
        </pre>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCopy} color="default" startIcon={<FileCopyIcon />}>
          {copied ? 'Copied!' : 'Copy'}
        </Button>
        <Button onClick={onClose} color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

const TOOL_LABELS = {
  query_datawarehouse: 'Querying data warehouse',
  fetch_ads_txt: 'Fetching ads.txt',
  lookup_domain: 'Looking up domain',
  query_customer_database: 'Querying customer database',
  lookup_ip: 'Looking up IP address',
};

function IvtAnalysisDialog({ open, onClose, result, loading, row, progress, mode }) {
  const [copied, setCopied] = useState(false);
  const [showPayload, setShowPayload] = useState(false);
  const progressEndRef = React.useRef(null);

  React.useEffect(() => {
    if (progressEndRef.current) {
      progressEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [progress]);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(result, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const tierColor = result ? (TIER_COLORS[result.tier] || '#999') : '#999';
  const tierLabel = result ? (result.tier_label || TIER_LABELS[result.tier] || 'Unknown') : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {mode === 'rules' ? <PolicyIcon /> : <AIIcon />}
        IVT Analysis {mode === 'rules' ? '' : '(AI powered)'}
        {result && result.tier >= 0 && (
          <Chip
            label={`Tier ${result.tier}: ${tierLabel}`}
            style={{ backgroundColor: tierColor, color: '#fff', fontWeight: 'bold', marginLeft: '10px' }}
          />
        )}
        {result && result.domain_trust && result.domain_trust.ranked && (
          <Chip
            size="small"
            label={`Ranking #${result.domain_trust.rank.toLocaleString()} (${result.domain_trust.trust_tier})`}
            style={{
              backgroundColor: result.domain_trust.trust_tier === 'high' ? '#4caf50' : result.domain_trust.trust_tier === 'moderate' ? '#2196f3' : '#9e9e9e',
              color: '#fff',
              fontSize: '11px',
              marginLeft: '6px'
            }}
          />
        )}
      </DialogTitle>
      <DialogContent>
        {row && (
          <Box style={{ marginBottom: '16px' }}>
            <Button
              size="small"
              color="default"
              onClick={() => setShowPayload(!showPayload)}
              style={{ textTransform: 'none', padding: 0, marginBottom: '4px' }}
            >
              {showPayload ? '▼' : '▶'} Analyzed record - {row.publisher_name || row.publisher} - {row.tldomain} {row.bidder ? ` - bidder: ${row.bidder_name}` : ''} ({row.timestamp.substring(0, 19)})
            </Button>
            {showPayload && (
              <pre style={{ ...jsonPreStyle, maxHeight: '300px', margin: 0 }}>
{formatRowDetails(row)}
              </pre>
            )}
            <Divider style={{ marginTop: '12px' }} />
          </Box>
        )}
        {loading && (
          <Box style={{ padding: '16px 0' }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="textSecondary">
                Agent is analyzing
              </Typography>
            </Box>
            {progress && progress.length > 0 && (
              <Box style={{ backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '8px 12px', maxHeight: '200px', overflow: 'auto', fontSize: '12px', fontFamily: 'monospace' }}>
                {progress.map((p, i) => (
                  <div key={i} style={{ padding: '2px 0', color: p.event === 'tool_call' ? '#1976d2' : p.event === 'tool_result' ? '#388e3c' : p.event === 'thinking' ? '#777' : '#333' }}>
                    {p.event === 'tool_call' && `> ${TOOL_LABELS[p.tool] || p.tool}: ${p.detail}`}
                    {p.event === 'tool_result' && `  ✓ ${p.tool} — ${p.detail}`}
                    {p.event === 'thinking' && `  ${p.detail}`}
                    {p.event === 'status' && p.detail}
                  </div>
                ))}
                <div ref={progressEndRef} />
              </Box>
            )}
          </Box>
        )}
        {!loading && result && result.error && (
          <Typography color="error" style={{ padding: '20px 0' }}>{result.error}</Typography>
        )}
        {!loading && result && !result.error && (
          <div>
            {/* Signals */}
            {result.signals && result.signals.length > 0 && (
              <Box style={{ marginBottom: '16px' }}>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Signals Detected</Typography>
                {result.signals.map((signal, i) => (
                  <Box key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Chip
                      size="small"
                      label={signal.code}
                      style={{ backgroundColor: TIER_COLORS[signal.tier] || '#999', color: '#fff', fontSize: '11px', fontFamily: 'monospace' }}
                    />
                    <Typography variant="body2">{signal.detail}</Typography>
                  </Box>
                ))}
              </Box>
            )}
            {result.signals && result.signals.length === 0 && (
              <Box style={{ marginBottom: '16px' }}>
                <Chip size="small" label="No IVT signals detected" style={{ backgroundColor: '#4caf50', color: '#fff' }} />
              </Box>
            )}

            {/* MFA Assessment */}
            {result.mfa_assessment && (
              <Box style={{ marginBottom: '16px' }}>
                <Divider style={{ margin: '8px 0' }} />
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>MFA Assessment</Typography>
                <Typography variant="body2">
                  {result.mfa_assessment.is_mfa ? 'MFA Detected' : 'Not MFA'} (confidence: {result.mfa_assessment.confidence})
                </Typography>
                {result.mfa_assessment.detail && (
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px' }}>{result.mfa_assessment.detail}</Typography>
                )}
              </Box>
            )}

            {/* SPO Assessment */}
            {result.spo_assessment && (
              <Box style={{ marginBottom: '16px' }}>
                <Divider style={{ margin: '8px 0' }} />
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>SPO Assessment</Typography>
                <Typography variant="body2">
                  {result.spo_assessment.is_inefficient ? 'Inefficient Supply Path' : 'Efficient Supply Path'} (confidence: {result.spo_assessment.confidence})
                  {result.spo_assessment.schain_depth > 0 && ` — ${result.spo_assessment.schain_depth} nodes in supply chain`}
                </Typography>
                {result.spo_assessment.detail && (
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px' }}>{result.spo_assessment.detail}</Typography>
                )}
              </Box>
            )}

            {/* Reasoning */}
            {result.reasoning && (
              <Box style={{ marginBottom: '16px' }}>
                <Divider style={{ margin: '8px 0' }} />
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Reasoning</Typography>
                <Typography variant="body2">{result.reasoning}</Typography>
              </Box>
            )}

            {/* Investigation Notes */}
            {result.investigation_notes && (
              <Box style={{ marginBottom: '16px' }}>
                <Divider style={{ margin: '8px 0' }} />
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Investigation Notes</Typography>
                <pre style={{ backgroundColor: '#f5f5f5', color: '#333333', padding: '12px', borderRadius: '4px', overflow: 'auto', maxHeight: '200px', fontSize: '11px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {result.investigation_notes}
                </pre>
              </Box>
            )}

            {/* Rules evaluated */}
            {result.rules_evaluated && (
              <Box style={{ marginBottom: '8px' }}>
                <Divider style={{ margin: '8px 0' }} />
                <Typography variant="caption" color="textSecondary">
                  Rules evaluated: {result.rules_evaluated.join(', ')}
                </Typography>
              </Box>
            )}
            {result.rules_not_evaluated && result.rules_not_evaluated.length > 0 && (
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Not evaluated: {result.rules_not_evaluated.join(', ')}
                </Typography>
              </Box>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {result && !loading && (
          <Button onClick={handleCopy} color="default" startIcon={<FileCopyIcon />}>
            {copied ? 'Copied!' : 'Copy Results'}
          </Button>
        )}
        <Button onClick={onClose} color="primary">{loading ? 'Cancel' : 'Close'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function PublisherScanDialog({ open, onClose, summary, loading, progress }) {
  const [copied, setCopied] = useState(false);
  const [viewOriginalData, setViewOriginalData] = useState(null);
  const [tierFilter, setTierFilter] = useState(null);
  const progressEndRef = React.useRef(null);

  React.useEffect(() => {
    if (progressEndRef.current) {
      progressEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [progress]);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(summary, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const riskColor = summary ? getRiskScoreColor(summary.risk_summary.risk_score) : '#999';

  // Extract latest analyze progress for the progress bar
  const latestAnalyzeProgress = progress && progress.length > 0
    ? [...progress].reverse().find(p => p.phase === 'analyze' && p.progress && p.total)
    : null;
  const currentPhase = progress && progress.length > 0 ? progress[progress.length - 1].phase : null;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <AssessmentIcon />
        Publisher Traffic Analysis
        {summary && (
          <>
            <Chip
              label={`Risk: ${summary.risk_summary.risk_score}%`}
              style={{ backgroundColor: riskColor, color: '#fff', fontWeight: 'bold', marginLeft: '10px' }}
            />
            <Chip
              size="small"
              label={`IVT Rate: ${summary.risk_summary.ivt_rate}%`}
              style={{ backgroundColor: getIvtRateColor(summary.risk_summary.ivt_rate), color: '#fff', marginLeft: '6px' }}
            />
          </>
        )}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box style={{ padding: '16px 0' }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="textSecondary">
                {currentPhase === 'query' && 'Querying records from ClickHouse...'}
                {currentPhase === 'analyze' && 'Analyzing records...'}
                {currentPhase === 'summarize' && 'Building summary...'}
                {!currentPhase && 'Scanning publisher traffic...'}
              </Typography>
            </Box>
            {latestAnalyzeProgress && (
              <Box style={{ marginBottom: '12px' }}>
                <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Typography variant="caption" color="textSecondary">
                    {latestAnalyzeProgress.detail}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {latestAnalyzeProgress.progress}/{latestAnalyzeProgress.total}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(latestAnalyzeProgress.progress / latestAnalyzeProgress.total) * 100}
                  style={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}
            {!latestAnalyzeProgress && currentPhase === 'query' && (
              <LinearProgress style={{ marginBottom: '12px', height: 8, borderRadius: 4 }} />
            )}
            {progress.length > 0 && (
              <Box style={{ backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '8px 12px', maxHeight: '150px', overflow: 'auto', fontSize: '12px', fontFamily: 'monospace' }}>
                {progress.map((p, i) => (
                  <div key={i} style={{ padding: '2px 0', color: p.phase === 'summarize' ? '#1976d2' : p.phase === 'complete' ? '#388e3c' : '#555' }}>
                    [{p.phase}] {p.detail}
                  </div>
                ))}
                <div ref={progressEndRef} />
              </Box>
            )}
          </Box>
        )}
        {!loading && summary && (
          <div>
            {/* Sample Size */}
            <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '4px' }}>Sample Size</Typography>
            <Typography variant="body2" style={{ marginBottom: '12px' }}>
              {summary.sample_size.total} records ({summary.sample_size.incoming_request} incoming, {summary.sample_size.bidrequest} bid requests)
            </Typography>

            {/* Tier Distribution */}
            <Divider style={{ margin: '8px 0' }} />
            <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Tier Distribution</Typography>
            {summary.risk_summary.tier_distribution.map(td => (
              <Box key={td.tier} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <Chip
                  size="small"
                  label={`Tier ${td.tier}`}
                  style={{ backgroundColor: TIER_COLORS[td.tier] || '#999', color: '#fff', fontSize: '11px', width: '60px' }}
                />
                <Typography variant="body2" style={{ width: '100px' }}>{td.label}</Typography>
                <Box style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <LinearProgress
                    variant="determinate"
                    value={td.percentage}
                    style={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e0e0e0' }}
                  />
                  <Typography variant="body2" style={{ minWidth: '80px' }}>{td.count} ({td.percentage}%)</Typography>
                </Box>
              </Box>
            ))}

            {/* Top Signals */}
            {summary.top_signals && summary.top_signals.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0 8px' }} />
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Top Signals</Typography>
                {summary.top_signals.slice(0, 10).map((sig, i) => (
                  <Box key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Chip
                      size="small"
                      label={sig.code}
                      style={{ backgroundColor: TIER_COLORS[sig.tier] || '#999', color: '#fff', fontSize: '11px', fontFamily: 'monospace' }}
                    />
                    <Typography variant="body2">
                      {sig.count}x &mdash; Rule {sig.rule}
                    </Typography>
                    {sig.examples && sig.examples[0] && (
                      <Typography variant="caption" color="textSecondary" style={{ marginLeft: '4px' }}>
                        e.g. {sig.examples[0].substring(0, 80)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </>
            )}

            {/* MFA & SPO */}
            <Divider style={{ margin: '12px 0 8px' }} />
            <Box style={{ display: 'flex', gap: '24px' }}>
              <Box>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>MFA</Typography>
                <Typography variant="body2">{summary.mfa_summary.flagged_count} flagged ({summary.mfa_summary.flagged_rate}%)</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>SPO</Typography>
                <Typography variant="body2">{summary.spo_summary.flagged_count} flagged ({summary.spo_summary.flagged_rate}%)</Typography>
              </Box>
            </Box>

            {/* Domain Breakdown */}
            {summary.domain_breakdown && summary.domain_breakdown.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0 8px' }} />
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Domain Breakdown</Typography>
                <Box style={{ maxHeight: '200px', overflow: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e0e0e0', textAlign: 'left' }}>
                        <th style={{ padding: '4px 8px' }}>Domain</th>
                        <th style={{ padding: '4px 8px' }}>Records</th>
                        <th style={{ padding: '4px 8px' }}>Pass</th>
                        <th style={{ padding: '4px 8px' }}>Suspicious</th>
                        <th style={{ padding: '4px 8px' }}>Likely IVT</th>
                        <th style={{ padding: '4px 8px' }}>Confirmed</th>
                        <th style={{ padding: '4px 8px' }}>Signals</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.domain_breakdown.slice(0, 20).map((d, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                            {d.domain}
                            {d.domainTrust && d.domainTrust.ranked && (
                              <Chip size="small" label={`#${d.domainTrust.rank.toLocaleString()}`}
                                style={{ marginLeft: '4px', fontSize: '10px', height: '18px',
                                  backgroundColor: d.domainTrust.trust_tier === 'high' ? '#4caf50' : d.domainTrust.trust_tier === 'moderate' ? '#2196f3' : '#9e9e9e',
                                  color: '#fff' }} />
                            )}
                          </td>
                          <td style={{ padding: '4px 8px' }}>{d.records}</td>
                          <td style={{ padding: '4px 8px', color: '#4caf50' }}>{d.tiers[0] || 0}</td>
                          <td style={{ padding: '4px 8px', color: '#ff9800' }}>{d.tiers[1] || 0}</td>
                          <td style={{ padding: '4px 8px', color: '#f44336' }}>{d.tiers[2] || 0}</td>
                          <td style={{ padding: '4px 8px', color: '#9c27b0' }}>{d.tiers[3] || 0}</td>
                          <td style={{ padding: '4px 8px' }}>{d.signals}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </>
            )}

            {/* All Records */}
            {summary.all_records && summary.all_records.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0 8px' }} />
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                  All Records ({summary.all_records.filter(r => tierFilter === null || r.tier === tierFilter).length}{tierFilter !== null ? ` of ${summary.all_records.length}` : ''})
                </Typography>
                <Box style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <Chip size="small" label="All" variant={tierFilter === null ? 'default' : 'outlined'}
                    onClick={() => setTierFilter(null)}
                    style={tierFilter === null ? { backgroundColor: '#1976d2', color: '#fff' } : {}} />
                  {[0, 1, 2, 3].map(t => {
                    const count = summary.all_records.filter(r => r.tier === t).length;
                    return (
                      <Chip key={t} size="small" label={`${TIER_LABELS[t]} (${count})`}
                        variant={tierFilter === t ? 'default' : 'outlined'}
                        onClick={() => setTierFilter(tierFilter === t ? null : t)}
                        style={tierFilter === t ? { backgroundColor: TIER_COLORS[t], color: '#fff' } : {}} />
                    );
                  })}
                </Box>
                <Box style={{ maxHeight: '200px', overflow: 'auto' }}>
                  {summary.all_records.filter(r => tierFilter === null || r.tier === tierFilter).map((f, i) => (
                    <Box key={i} style={{ marginBottom: '8px', padding: '6px 8px', backgroundColor: '#fafafa', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <Chip size="small" label={f.tier_label}
                          style={{ backgroundColor: TIER_COLORS[f.tier] || '#999', color: '#fff', fontSize: '11px' }} />
                        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                          {f.tldomain} ({f.ip || 'no IP'})
                        </Typography>
                      </Box>
                      {f.signals.map((s, j) => (
                        <Typography key={j} variant="caption" style={{ display: 'block', color: '#555', paddingLeft: '8px' }}>
                          {s.code}: {s.detail}
                        </Typography>
                      ))}
                      {f.data && (
                        <Typography variant="caption" style={{ display: 'block', paddingLeft: '8px', marginTop: '2px' }}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setViewOriginalData(f); }}
                            style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'none' }}>
                            View Original {f.type === 'bidrequest' ? '(Bid Request)' : f.type === 'incoming_request' ? '(Incoming)' : ''}
                          </a>
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </>
            )}
            <JsonViewerDialog
              open={!!viewOriginalData}
              onClose={() => setViewOriginalData(null)}
              data={viewOriginalData ? formatRowDetails(viewOriginalData) : ''}
              title={viewOriginalData ? `Original Record — ${viewOriginalData.tldomain} (${viewOriginalData.type || 'unknown'})` : 'Original Record'}
            />

            <Divider style={{ margin: '12px 0 8px' }} />
            <Typography variant="caption" color="textSecondary">
              Scanned at {new Date(summary.scan_timestamp).toLocaleString()}
            </Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {summary && !loading && (
          <Button onClick={handleCopy} color="default" startIcon={<FileCopyIcon />}>
            {copied ? 'Copied!' : 'Copy Results'}
          </Button>
        )}
        <Button onClick={onClose} color="primary" disabled={loading}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function TrafficTable({ entityType, entityId, trafficType, days, refreshKey, siteFilter, searchText }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState('');
  const [dialogTitle, setDialogTitle] = useState('');
  const [ivtDialogOpen, setIvtDialogOpen] = useState(false);
  const [ivtResult, setIvtResult] = useState(null);
  const [ivtLoading, setIvtLoading] = useState(false);
  const [ivtRow, setIvtRow] = useState(null);
  const [ivtProgress, setIvtProgress] = useState([]);
  const [ivtMode, setIvtMode] = useState('ai');

  // Debounce site filter to avoid firing API calls on every keystroke
  const [debouncedSiteFilter, setDebouncedSiteFilter] = useState(siteFilter);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSiteFilter(siteFilter), 500);
    return () => clearTimeout(timer);
  }, [siteFilter]);

  const fetchData = useCallback(() => {
    if (!entityId) return;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('type', trafficType);
    if (entityType === 'publisher') {
      params.set('publisher_id', entityId);
      if (debouncedSiteFilter && debouncedSiteFilter.trim()) {
        params.set('site_id', debouncedSiteFilter.trim());
      }
    } else if (entityType === 'bidder') {
      params.set('bidder_id', entityId);
    } else if (entityType === 'site') {
      params.set('site_id', entityId);
    }
    params.set('days', days);
    params.set('end', '200');

    const url = `${window.nobid_endpoint}/traffic?${params.toString()}`;
    const options = { headers: new Headers() };
    options.headers.set('accept', 'application/json');
    const token = localStorage.getItem('token');
    options.headers.set('Authorization', `Bearer ${token}`);

    fetch(url, options)
      .then(response => response.json())
      .then(results => {
        setData(Array.isArray(results) ? results : []);
        setLoading(false);
      })
      .catch(() => {
        setData([]);
        setLoading(false);
      });
  }, [entityType, entityId, trafficType, days, refreshKey, debouncedSiteFilter]);

  // Client-side text search filtering
  const filteredData = useMemo(() => {
    if (!searchText || !searchText.trim()) return data;
    const term = searchText.trim().toLowerCase();
    return data.filter(row => {
      const dataStr = typeof row.data === 'string' ? row.data : JSON.stringify(row.data || '');
      return dataStr.toLowerCase().includes(term);
    });
  }, [data, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleViewData = (row) => {
    setDialogData(formatRowDetails(row));
    setDialogTitle(`${TYPE_LABELS[trafficType]} - ${row.timestamp}`);
    setDialogOpen(true);
  };

  const handleAnalyzeIvt = async (row, mode = 'ai') => {
    if (!row.data) return;
    setIvtResult(null);
    setIvtRow(row);
    setIvtProgress([]);
    setIvtLoading(true);
    setIvtMode(mode);
    setIvtDialogOpen(true);

    const url = `${window.nobid_endpoint}/ivt_analysis`;
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          payload: row.data,
          mode,
          context: {
            type: row.type,
            endpoint: row.endpoint,
            publisher: row.publisher,
            publisher_name: row.publisher_name,
            site: row.site,
            site_name: row.site_name,
            bidder: row.bidder,
            bidder_name: row.bidder_name,
            tldomain: row.tldomain,
            ip: row.ip,
            integration: row.integration,
          },
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const parsed = JSON.parse(line.slice(6));
            if (parsed.event === 'result') {
              setIvtResult(parsed.data);
              setIvtLoading(false);
            } else if (parsed.event === 'error') {
              setIvtResult({ error: parsed.detail, tier: -1, signals: [] });
              setIvtLoading(false);
            } else {
              setIvtProgress(prev => [...prev, parsed]);
            }
          } catch (e) {
            // skip malformed SSE lines
          }
        }
      }
    } catch (err) {
      setIvtResult({ error: err.message, tier: -1, signals: [] });
      setIvtLoading(false);
    }
  };

  const columns = [
    { name: 'Timestamp', selector: 'timestamp', sortable: true, Xwidth: '180px' },
    {
      name: 'Publisher', selector: 'publisher', sortable: true, Xwidth: '140px',
      cell: row => row.publisher > 0 ? (row.publisher_name ? `${row.publisher_name} (${row.publisher})` : row.publisher) : ''
    },
    {
      name: 'Site', selector: 'site', sortable: true, Xwidth: '140px',
      cell: row => row.site > 0 ? (row.site_name ? `${row.site_name} (${row.site})` : row.site) : ''
    },
    { name: 'Domain', selector: 'tldomain', sortable: true, Xwidth: '140px' },
    { name: 'IP', selector: 'ip', sortable: true, Xwidth: '120px', cell: row => row.ip || '' },
    {
      name: 'Bidder', selector: 'bidder', sortable: true, Xwidth: '140px',
      cell: row => row.bidder > 0 ? (row.bidder_name ? `${row.bidder_name} (${row.bidder})` : row.bidder) : ''
    },
    {
      name: 'Data',
      selector: 'id',
      grow: 2,
      cell: row => {
        if (!row.data) return '';
        const preview = typeof row.data === 'string' ? row.data.substring(0, MAX_DTA_PREVIEW_LENGTH) : JSON.stringify(row.data).substring(0, MAX_DTA_PREVIEW_LENGTH);
        return (
          <span
            onClick={() => handleViewData(row)}
            title="Click to view full data"
            style={{ cursor: 'pointer', color: '#1976d2', fontSize: '11px', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '500px', display: 'inline-block' }}
          >
            {preview}...
          </span>
        );
      },
    },
    { name: 'Integration', selector: 'integration', sortable: true, grow: 2 },
    // { name: 'Endpoint', selector: 'endpoint', sortable: true, grow: 2 },
    // { name: 'Method', selector: 'method', sortable: true, width: '80px' },
    {
      name: 'Actions',
      width: '160px',
      cell: row => row.data ? (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Button
            Xvariant="outlined"
            size="small"
            onClick={() => handleViewData(row)}
            title="View Request"
            style={{ minWidth: '40px', padding: '2px 6px' }}
            color="primary"
          >
            <VisibilityIcon style={{ fontSize: '18px' }} variant="contained" color="primary"/>
          </Button>
          <Button
            Xvariant="outlined"
            size="small"
            onClick={() => handleAnalyzeIvt(row, 'rules')}
            title="IVT Analysis"
            style={{ minWidth: '40px', padding: '2px 6px' }}
            color="default"
          >
            <PolicyIcon style={{ fontSize: '18px' }} variant="contained" color="primary"/>
          </Button>
          <Button
            Xvariant="outlined"
            size="small"
            onClick={() => handleAnalyzeIvt(row, 'ai')}
            title="IVT Analysis (AI Powered)"
            style={{ minWidth: '40px', padding: '2px 6px' }}
            color="primary"
          >
            <AIIcon style={{ fontSize: '18px' }} variant="contained" color="primary"/>
          </Button>
        </div>
      ) : null,
    },
  ];

  const timeLabel = days === 1 ? 'last 24h' : `last ${days} days`;

  return (
    <div>
      {(searchText && searchText.trim()) && data.length > 0 && (
        <span style={{ fontSize: '12px', color: '#666', padding: '4px 0', display: 'block' }}>
          Showing {filteredData.length} of {data.length} records
        </span>
      )}
      <DataTable
        columns={columns}
        data={filteredData}
        progressPending={loading}
        pagination
        fixedHeader
        paginationPerPage={20}
        paginationRowsPerPageOptions={[10, 20, 50, 100]}
        dense
        highlightOnHover
        noDataComponent={
          searchText && searchText.trim() && data.length > 0
            ? <p>{`No records match "${searchText}"`}</p>
            : <p>{`No traffic data found for the ${timeLabel}`}</p>
        }
        noHeader
      />
      <JsonViewerDialog open={dialogOpen} onClose={() => setDialogOpen(false)} data={dialogData} title={dialogTitle} />
      <IvtAnalysisDialog open={ivtDialogOpen} onClose={() => setIvtDialogOpen(false)} result={ivtResult} loading={ivtLoading} row={ivtRow} progress={ivtProgress} mode={ivtMode} />
    </div>
  );
}

function TrafficDataSubTab({ entityType, entityId, trafficTypes, onScanClick, scanLoading }) {
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(0);
  const [days, setDays] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [siteFilter, setSiteFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setSiteFilter('');
    setSearchText('');
  }, [entityId]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '10px 0', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '14px', fontWeight: 500 }}>Time Range:</span>
        <Select value={days} onChange={(e) => setDays(e.target.value)} variant="outlined" style={{ minWidth: 180, height: 36 }}>
          {TIME_FRAME_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
        {entityType === 'publisher' && (
          <TextField
            variant="outlined"
            size="small"
            placeholder="Filter by Site ID"
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            style={{ width: 160 }}
            InputProps={{ style: { height: 36 } }}
          />
        )}
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search in data..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 220 }}
          InputProps={{
            style: { height: 36 },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ fontSize: 18, color: '#999' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => setRefreshKey(k => k + 1)}>
          Refresh
        </Button>
        {onScanClick && (
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={<PolicyIcon />}
            onClick={onScanClick}
            disabled={scanLoading}
          >
            Analyze Traffic
          </Button>
        )}
      </div>
      <AppBar position="static" color="default">
        <Tabs
          value={tabIndex}
          onChange={(e, newValue) => setTabIndex(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          {trafficTypes.map(type => (
            <Tab key={type} label={TYPE_LABELS[type] || type} />
          ))}
        </Tabs>
      </AppBar>
      {trafficTypes.map((type, index) => (
        <TabPanel key={type} value={tabIndex} index={index} dir={theme.direction}>
          <TrafficTable entityType={entityType} entityId={entityId} trafficType={type} days={days} refreshKey={refreshKey} siteFilter={siteFilter} searchText={searchText} />
        </TabPanel>
      ))}
    </div>
  );
}

function ScanSummaryCard({ summary, label }) {
  const [viewOriginalData, setViewOriginalData] = useState(null);
  const [tierFilter, setTierFilter] = useState(null);
  if (!summary) return null;
  const riskColor = getRiskScoreColor(summary.risk_summary.risk_score);

  return (
    <Paper variant="outlined" style={{ padding: '16px', marginBottom: '16px' }}>
      {label && <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>{label}</Typography>}
      <Box style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <Chip label={`Risk: ${summary.risk_summary.risk_score}%`}
          style={{ backgroundColor: riskColor, color: '#fff', fontWeight: 'bold' }} />
        <Chip size="small" label={`IVT Rate: ${summary.risk_summary.ivt_rate}%`}
          style={{ backgroundColor: getIvtRateColor(summary.risk_summary.ivt_rate), color: '#fff' }} />
        <Typography variant="body2" color="textSecondary">
          {summary.sample_size.total} records ({summary.sample_size.incoming_request} incoming, {summary.sample_size.bidrequest} bid)
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {new Date(summary.scan_timestamp).toLocaleString()}
        </Typography>
      </Box>

      {/* Tier Distribution */}
      <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '4px' }}>Tier Distribution</Typography>
      {summary.risk_summary.tier_distribution.map(td => (
        <Box key={td.tier} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <Chip size="small" label={`Tier ${td.tier}`}
            style={{ backgroundColor: TIER_COLORS[td.tier] || '#999', color: '#fff', fontSize: '11px', width: '60px' }} />
          <Typography variant="body2" style={{ width: '100px' }}>{td.label}</Typography>
          <Box style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LinearProgress variant="determinate" value={td.percentage}
              style={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e0e0e0' }} />
            <Typography variant="body2" style={{ minWidth: '80px' }}>{td.count} ({td.percentage}%)</Typography>
          </Box>
        </Box>
      ))}

      {/* Top Signals */}
      {summary.top_signals && summary.top_signals.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0 8px' }} />
          <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Top Signals</Typography>
          {summary.top_signals.slice(0, 10).map((sig, i) => (
            <Box key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Chip size="small" label={sig.code}
                style={{ backgroundColor: TIER_COLORS[sig.tier] || '#999', color: '#fff', fontSize: '11px', fontFamily: 'monospace' }} />
              <Typography variant="body2">{sig.count}x &mdash; Rule {sig.rule}</Typography>
              {sig.examples && sig.examples[0] && (
                <Typography variant="caption" color="textSecondary" style={{ marginLeft: '4px' }}>
                  e.g. {sig.examples[0].substring(0, 80)}
                </Typography>
              )}
            </Box>
          ))}
        </>
      )}

      {/* MFA & SPO */}
      <Divider style={{ margin: '12px 0 8px' }} />
      <Box style={{ display: 'flex', gap: '24px' }}>
        <Box>
          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>MFA</Typography>
          <Typography variant="body2">{summary.mfa_summary.flagged_count} flagged ({summary.mfa_summary.flagged_rate}%)</Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>SPO</Typography>
          <Typography variant="body2">{summary.spo_summary.flagged_count} flagged ({summary.spo_summary.flagged_rate}%)</Typography>
        </Box>
      </Box>

      {/* Domain Breakdown */}
      {summary.domain_breakdown && summary.domain_breakdown.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0 8px' }} />
          <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Domain Breakdown</Typography>
          <Box style={{ maxHeight: '200px', overflow: 'auto' }}>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e0e0e0', textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px' }}>Domain</th>
                  <th style={{ padding: '4px 8px' }}>Records</th>
                  <th style={{ padding: '4px 8px' }}>Pass</th>
                  <th style={{ padding: '4px 8px' }}>Suspicious</th>
                  <th style={{ padding: '4px 8px' }}>Likely IVT</th>
                  <th style={{ padding: '4px 8px' }}>Confirmed</th>
                  <th style={{ padding: '4px 8px' }}>Signals</th>
                </tr>
              </thead>
              <tbody>
                {summary.domain_breakdown.slice(0, 20).map((d, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                      {d.domain}
                      {d.domainTrust && d.domainTrust.ranked && (
                        <Chip size="small" label={`#${d.domainTrust.rank.toLocaleString()}`}
                          style={{ marginLeft: '4px', fontSize: '10px', height: '18px',
                            backgroundColor: d.domainTrust.trust_tier === 'high' ? '#4caf50' : d.domainTrust.trust_tier === 'moderate' ? '#2196f3' : '#9e9e9e',
                            color: '#fff' }} />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px' }}>{d.records}</td>
                    <td style={{ padding: '4px 8px', color: '#4caf50' }}>{d.tiers[0] || 0}</td>
                    <td style={{ padding: '4px 8px', color: '#ff9800' }}>{d.tiers[1] || 0}</td>
                    <td style={{ padding: '4px 8px', color: '#f44336' }}>{d.tiers[2] || 0}</td>
                    <td style={{ padding: '4px 8px', color: '#9c27b0' }}>{d.tiers[3] || 0}</td>
                    <td style={{ padding: '4px 8px' }}>{d.signals}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </>
      )}

      {/* All Records */}
      {summary.all_records && summary.all_records.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0 8px' }} />
          <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            All Records ({summary.all_records.filter(r => tierFilter === null || r.tier === tierFilter).length}{tierFilter !== null ? ` of ${summary.all_records.length}` : ''})
          </Typography>
          <Box style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <Chip size="small" label="All" variant={tierFilter === null ? 'default' : 'outlined'}
              onClick={() => setTierFilter(null)}
              style={tierFilter === null ? { backgroundColor: '#1976d2', color: '#fff' } : {}} />
            {[0, 1, 2, 3].map(t => {
              const count = summary.all_records.filter(r => r.tier === t).length;
              return (
                <Chip key={t} size="small" label={`${TIER_LABELS[t]} (${count})`}
                  variant={tierFilter === t ? 'default' : 'outlined'}
                  onClick={() => setTierFilter(tierFilter === t ? null : t)}
                  style={tierFilter === t ? { backgroundColor: TIER_COLORS[t], color: '#fff' } : {}} />
              );
            })}
          </Box>
          <Box style={{ maxHeight: '200px', overflow: 'auto' }}>
            {summary.all_records.filter(r => tierFilter === null || r.tier === tierFilter).map((f, i) => (
              <Box key={i} style={{ marginBottom: '8px', padding: '6px 8px', backgroundColor: '#fafafa', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <Chip size="small" label={f.tier_label}
                    style={{ backgroundColor: TIER_COLORS[f.tier] || '#999', color: '#fff', fontSize: '11px' }} />
                  <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                    {f.tldomain} ({f.ip || 'no IP'})
                  </Typography>
                </Box>
                {f.signals.map((s, j) => (
                  <Typography key={j} variant="caption" style={{ display: 'block', color: '#555', paddingLeft: '8px' }}>
                    {s.code}: {s.detail}
                  </Typography>
                ))}
                {f.data && (
                  <Typography variant="caption" style={{ display: 'block', paddingLeft: '8px', marginTop: '2px' }}>
                    <a href="#" onClick={(e) => { e.preventDefault(); setViewOriginalData(f); }}
                      style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'none' }}>
                      View Original {f.type === 'bidrequest' ? '(Bid Request)' : f.type === 'incoming_request' ? '(Incoming)' : ''}
                    </a>
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </>
      )}
      <JsonViewerDialog
        open={!!viewOriginalData}
        onClose={() => setViewOriginalData(null)}
        data={viewOriginalData ? formatRowDetails(viewOriginalData) : ''}
        title={viewOriginalData ? `Original Record — ${viewOriginalData.tldomain} (${viewOriginalData.type || 'unknown'})` : 'Original Record'}
      />
    </Paper>
  );
}

function LatestScanBanner({ scanHistory }) {
  const latestScan = scanHistory.length > 0 ? scanHistory[0] : null;
  if (!latestScan || !latestScan.summary) return null;

  return (
    <Box style={{ marginBottom: '16px' }}>
      <SectionHeader title="IVT Summary" />
      <IvtMetricBoxes scanHistory={scanHistory} />
    </Box>
  );
}

// ─── IVT Scan Legend ────────────────────────────────────────────────────────

const IVT_LEGEND_TIERS = [
  { tier: 0, label: 'Pass', color: '#4caf50', meaning: 'No issues detected — traffic appears legitimate' },
  { tier: 1, label: 'Suspicious', color: '#ff9800', meaning: 'Minor anomalies worth monitoring (VPN/proxy, geo mismatch, $0 bid floor)' },
  { tier: 2, label: 'Likely IVT', color: '#f44336', meaning: 'Strong indicators of invalid traffic (invalid UA, missing schain, device mismatch)' },
  { tier: 3, label: 'Confirmed IVT', color: '#9c27b0', meaning: 'Definitive invalid traffic (datacenter IP, known bot UA, spoofed domain)' },
];

const IVT_LEGEND_CATEGORIES = [
  { code: 'GI', name: 'General Impression', description: 'Core traffic validation: datacenter IP detection, bot/spider user agents, invalid UAs, missing supply chain, VPN/proxy/Tor detection, and geo mismatches.', source: 'IP intelligence (MaxMind, ipinfo), User-Agent analysis, OpenRTB schain object', standard: 'TAG IVT Guidelines: datacenter IPs and known bots are General IVT (GIVT). VPN/proxy is Sophisticated IVT (SIVT).' },
  { code: 'SI', name: 'Supply Integrity', description: 'App-specific fraud checks: missing device IDs (ifa) without opt-out, OS/UA device mismatches, and $0 bid floor detection.', source: 'OpenRTB device object, app/site fields', standard: 'IAB Tech Lab requires device.ifa for in-app. Missing ifa without lmt=1 indicates spoofed traffic.' },
  { code: 'SPO', name: 'Supply Path Optimization', description: 'Supply chain analysis: excessive intermediaries (5+ nodes), duplicate resellers, incomplete schain, reseller-heavy paths, and ads.txt authorization verification.', source: 'OpenRTB schain object, ads.txt files', standard: 'IAB SPO best practices: minimize intermediaries, verify seller authorization, ensure schain completeness.' },
  { code: 'MFA', name: 'Made-for-Advertising', description: 'MFA site detection: bloated ads.txt, high ad density, content recommendation widget referrers, pagination/slideshow patterns, and publisher ads.txt mismatches.', source: 'Ads.txt analysis, web content analysis, referrer inspection', standard: 'ANA/ISBA MFA criteria: excessive ads.txt entries (400+), high reseller ratios (>3:1), pagination abuse, content rec widgets (Taboola, Outbrain).' },
];

const IVT_LEGEND_METRICS = [
  { name: 'Risk Score', description: 'Weighted aggregate of all signal tiers across sampled traffic (0-100%). Higher = more suspicious. >50% = high risk, 20-50% = medium, <20% = low.', source: 'NoBid rule engine' },
  { name: 'IVT Rate', description: 'Percentage of sampled records classified as Tier 2 or Tier 3 (likely or confirmed invalid traffic). Industry benchmark: <5% is clean.', source: 'NoBid rule engine' },
  { name: 'MFA Rate', description: 'Percentage of traffic flagged as coming from Made-for-Advertising sites. Indicates low-quality inventory designed for ad revenue, not user engagement.', source: 'Ads.txt + web content analysis' },
  { name: 'SPO Rate', description: 'Percentage of traffic with supply path issues (unauthorized resellers, excessive intermediaries, missing schain). Lower is better.', source: 'OpenRTB schain + ads.txt verification' },
  { name: 'Sample Size', description: 'Number of requests analyzed in this scan. Larger samples provide more statistically reliable results.', source: 'NoBid historical data and analytics' },
];

function IvtScanLegend() {
  const [open, setOpen] = useState(false);

  const cellStyle = { padding: '4px 12px 4px 0', fontSize: '12px', verticalAlign: 'top' };
  const headerStyle = { ...cellStyle, fontWeight: 600, color: '#333', borderBottom: '1px solid #e0e0e0', paddingBottom: '6px' };
  const sectionTitle = { fontSize: '13px', fontWeight: 600, color: '#333', margin: '16px 0 8px 0' };

  return (
    <div style={{ marginTop: '24px' }}>
      <Box style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <HelpOutlineIcon style={{ fontSize: '16px', color: '#999', marginRight: '6px' }} />
        <Typography variant="subtitle2" color="textSecondary">Legend & Methodology</Typography>
        <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>
      <Collapse in={open}>
        <Box style={{ backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '16px 20px', marginTop: '8px', color: '#555' }}>

          {/* Tier Classification */}
          <div style={sectionTitle}>Tier Classification</div>
          <table style={{ borderCollapse: 'collapse', marginBottom: '8px' }}>
            <thead>
              <tr>
                <td style={headerStyle}>Tier</td>
                <td style={headerStyle}>Meaning</td>
              </tr>
            </thead>
            <tbody>
              {IVT_LEGEND_TIERS.map(t => (
                <tr key={t.tier}>
                  <td style={cellStyle}><Chip size="small" label={t.label} style={{ backgroundColor: t.color, color: '#fff', fontSize: '11px' }} /></td>
                  <td style={cellStyle}>{t.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: '8px' }}>
            Each traffic record is classified into the highest tier triggered by its signals. Risk score is a weighted aggregate across all sampled records.
          </Typography>

          {/* Signal Categories */}
          <div style={sectionTitle}>Signal Categories</div>
          <table style={{ borderCollapse: 'collapse', marginBottom: '8px' }}>
            <thead>
              <tr>
                <td style={headerStyle}>Category</td>
                <td style={headerStyle}>What It Detects</td>
                <td style={headerStyle}>Data Sources</td>
                <td style={headerStyle}>Industry Standard</td>
              </tr>
            </thead>
            <tbody>
              {IVT_LEGEND_CATEGORIES.map(c => (
                <tr key={c.code}>
                  <td style={{ ...cellStyle, fontWeight: 500, whiteSpace: 'nowrap' }}>{c.code} — {c.name}</td>
                  <td style={cellStyle}>{c.description}</td>
                  <td style={cellStyle}>{c.source}</td>
                  <td style={cellStyle}>{c.standard}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Key Metrics */}
          <div style={sectionTitle}>Key Metrics</div>
          <table style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <td style={headerStyle}>Metric</td>
                <td style={headerStyle}>Description</td>
                <td style={headerStyle}>Source</td>
              </tr>
            </thead>
            <tbody>
              {IVT_LEGEND_METRICS.map(m => (
                <tr key={m.name}>
                  <td style={{ ...cellStyle, fontWeight: 500, whiteSpace: 'nowrap' }}>{m.name}</td>
                  <td style={cellStyle}>{m.description}</td>
                  <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{m.source}</td>
                </tr>
              ))}
            </tbody>
          </table>

        </Box>
      </Collapse>
    </div>
  );
}

function IvtScanHistorySubTab({ entityId, scanDialogOpen, setScanDialogOpen, scanSummary, scanLoading, scanProgress, onScanClick, scanHistory, historyLoading, fetchHistory }) {
  const [selectedScan, setSelectedScan] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);

  const handleClearHistory = async () => {
    setClearLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = `${window.nobid_endpoint}/ivt_publisher_scan/history?publisher_code=${entityId}`;
      const res = await fetch(url, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to clear history');
      setClearDialogOpen(false);
      fetchHistory();
    } catch (e) {
      console.error('Failed to clear scan history:', e);
    } finally {
      setClearLoading(false);
    }
  };

  return (
    <div style={{ padding: '10px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '16px' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchHistory}
          disabled={historyLoading}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          startIcon={<PolicyIcon />}
          onClick={onScanClick}
          disabled={scanLoading}
        >
          Analyze Traffic
        </Button>
        {scanHistory.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            style={{ color: '#f44336', borderColor: '#f44336' }}
            startIcon={<DeleteSweepIcon />}
            onClick={() => setClearDialogOpen(true)}
            disabled={clearLoading}
          >
            Clear History
          </Button>
        )}
        {scanHistory.length > 0 && (
          <Typography variant="body2" color="textSecondary">
            {scanHistory.length} scan{scanHistory.length !== 1 ? 's' : ''} on record
          </Typography>
        )}
      </div>

      {historyLoading && <LinearProgress style={{ marginBottom: '12px' }} />}

      {/* Latest Scan Details */}
      {scanHistory.length > 0 && scanHistory[0].summary && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="h6" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            Latest Scan
            <Typography variant="caption" color="textSecondary">
              {new Date(scanHistory[0].scan_timestamp).toLocaleString()}
            </Typography>
          </Typography>
          <ScanSummaryCard summary={scanHistory[0].summary} />
        </Box>
      )}

      {/* Scan History Table */}
      {scanHistory.length > 1 && (
        <Box>
          <Typography variant="h6" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HistoryIcon /> Scan History
          </Typography>
          <DataTable
            columns={[
              { name: 'Date', selector: 'scan_timestamp', sortable: true,
                cell: row => new Date(row.scan_timestamp).toLocaleString() },
              { name: 'Risk Score', selector: 'risk_score', sortable: true,
                cell: row => (
                  <Chip size="small" label={`${row.risk_score}%`}
                    style={{ backgroundColor: getRiskScoreColor(row.risk_score), color: '#fff', fontWeight: 'bold' }} />
                ) },
              { name: 'IVT Rate', selector: 'ivt_rate', sortable: true,
                cell: row => (
                  <Chip size="small" label={`${row.ivt_rate}%`}
                    style={{ backgroundColor: getIvtRateColor(row.ivt_rate), color: '#fff' }} />
                ) },
              { name: 'MFA Rate', selector: 'mfa_rate', sortable: true,
                cell: row => `${row.mfa_rate}%` },
              { name: 'SPO Rate', selector: 'spo_rate', sortable: true,
                cell: row => `${row.spo_rate}%` },
              { name: 'Sample', selector: 'sample_size', sortable: true },
              { name: '', width: '100px',
                cell: row => (
                  <Button size="small" color="primary" onClick={() => setSelectedScan(row.summary)}>
                    Details
                  </Button>
                ) },
            ]}
            data={scanHistory}
            pagination={false}
            dense
            highlightOnHover
            noHeader
          />
        </Box>
      )}

      {scanHistory.length === 0 && !historyLoading && (
        <Paper variant="outlined" style={{ padding: '32px', textAlign: 'center' }}>
          <AssessmentIcon style={{ fontSize: '48px', color: '#ccc', marginBottom: '8px' }} />
          <Typography variant="body1" color="textSecondary">
            No scans yet. Click "Analyze Publisher Traffic" to run the first scan.
          </Typography>
        </Paper>
      )}

      {/* Scan detail dialog for historical scans */}
      <Dialog open={!!selectedScan} onClose={() => setSelectedScan(null)} maxWidth="md" fullWidth>
        <DialogTitle>Scan Details</DialogTitle>
        <DialogContent>
          {selectedScan && <ScanSummaryCard summary={selectedScan} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedScan(null)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Clear history confirmation dialog */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)}>
        <DialogTitle>Clear Scan History</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to clear all scan history for this publisher? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)} disabled={clearLoading}>Cancel</Button>
          <Button onClick={handleClearHistory} style={{ color: '#f44336' }} disabled={clearLoading}>
            {clearLoading ? <CircularProgress size={20} /> : 'Clear All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Legend & Methodology */}
      <IvtScanLegend />

    </div>
  );
}

export default function TrafficPanel({ entityType, entityId, entityName, trafficTypes }) {
  const classes = useStyles();
  const [topTab, setTopTab] = useState(0);
  const showIvtTab = entityType === 'publisher';

  // Scan state lifted to parent so both sub-tabs can access it
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanSummary, setScanSummary] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanProgress, setScanProgress] = useState([]);

  // Scan history lifted from IvtScanHistorySubTab so LatestScanBanner can access it
  const [scanHistory, setScanHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = useCallback(() => {
    if (!entityId) return;
    setHistoryLoading(true);
    const url = `${window.nobid_endpoint}/ivt_publisher_scan/history?publisher_code=${entityId}&limit=20`;
    const token = localStorage.getItem('token');
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        setScanHistory(Array.isArray(data) ? data : []);
        setHistoryLoading(false);
        window.dispatchEvent(new CustomEvent('ivt-scan-updated'));
      })
      .catch(() => {
        setScanHistory([]);
        setHistoryLoading(false);
      });
  }, [entityId]);

  useEffect(() => {
    if (showIvtTab) fetchHistory();
  }, [showIvtTab, fetchHistory]);

  // Refresh history when a scan completes
  useEffect(() => {
    if (scanSummary && !scanLoading) {
      fetchHistory();
    }
  }, [scanSummary, scanLoading, fetchHistory]);

  const handlePublisherScan = async () => {
    setScanSummary(null);
    setScanProgress([]);
    setScanLoading(true);
    setScanDialogOpen(true);

    const url = `${window.nobid_endpoint}/ivt_publisher_scan`;
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          publisher_code: entityId,
          publisher_name: entityName || 'Unknown',
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const parsed = JSON.parse(line.slice(6));
            if (parsed.event === 'result') {
              setScanSummary(parsed.data);
              setScanLoading(false);
            } else if (parsed.event === 'error') {
              setScanSummary({ error: parsed.detail });
              setScanLoading(false);
            } else {
              setScanProgress(prev => [...prev, parsed]);
            }
          } catch (e) {
            // skip malformed SSE lines
          }
        }
      }
    } catch (err) {
      setScanSummary({ error: err.message });
      setScanLoading(false);
    }
  };

  return (
    <div className={classes.root} style={{ width: '100%', margin: '20px auto' }}>
      {showIvtTab ? (
        <>
          <LatestScanBanner scanHistory={scanHistory} />
          <AppBar position="static" color="default" elevation={1}>
            <Tabs
              value={topTab}
              onChange={(e, v) => setTopTab(v)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Traffic Data" icon={<SwapHorizIcon style={{ fontSize: '18px' }} />} />
              <Tab label="IVT Scan History" icon={<AssessmentIcon style={{ fontSize: '18px' }} />} />
            </Tabs>
          </AppBar>
          <TabPanel value={topTab} index={0}>
            <TrafficDataSubTab entityType={entityType} entityId={entityId} trafficTypes={trafficTypes} onScanClick={handlePublisherScan} scanLoading={scanLoading} />
          </TabPanel>
          <TabPanel value={topTab} index={1}>
            <IvtScanHistorySubTab
              entityId={entityId}
              scanDialogOpen={scanDialogOpen}
              setScanDialogOpen={setScanDialogOpen}
              scanSummary={scanSummary}
              scanLoading={scanLoading}
              scanProgress={scanProgress}
              onScanClick={handlePublisherScan}
              scanHistory={scanHistory}
              historyLoading={historyLoading}
              fetchHistory={fetchHistory}
            />
          </TabPanel>
          <PublisherScanDialog
            open={scanDialogOpen}
            onClose={() => setScanDialogOpen(false)}
            summary={scanSummary}
            loading={scanLoading}
            progress={scanProgress}
          />
        </>
      ) : (
        <TrafficDataSubTab entityType={entityType} entityId={entityId} trafficTypes={trafficTypes} />
      )}
    </div>
  );
}
